import React, {useState} from "react";
import {Button, Input, Switch, Table} from "antd";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {useStore} from "../../../../../hooks/useStore";
import {subCompanyShiftOptionsTypes} from "./SubCompanyShiftOptionsTypes";
import {SubCompanyContainer} from "../../optionsStyles";
import {EmptyMessage} from "../../../../_defaultComponents/EmptyMessage/EmptyMessage";

const SubCompanyShiftOptions = ({
    subCompanyData,
    subCompanyLoading,
    currentPage,
    setCurrentPage,
    companyId,
    subCompanyRecallRef,
    setSubCompanyRecall,
    setSubCompanyData
}: subCompanyShiftOptionsTypes) => {
    const {t} = useTranslation();
    const history = useHistory();
    const stores = useStore();
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);

    const openErrorModal = (submitData, key) => {
        stores.appStore.openErrorModal({
            title: `${t("modal.errorTitle", {ns: "common"})}!`,
            content: `${submitData.name}, ${t(`fields.shiftOptions.${key}.label`, {
                ns: "common"
            })} ${t("validation.greaterThanZero", {
                ns: "common"
            })}`,
            onOk: () => {}
        });
    };
    const onSubCompanySubmit = index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.shift;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;

        const submitKeys = Object.keys(submitData);
        let hasError = false;

        submitKeys.forEach(key => {
            if (["shiftControlIsActive", "distanceAlertIsActive"].includes(key)) return;
            if (key === "distanceLimit" && submitData["distanceAlertIsActive"] === false) return;

            if (submitData[key] <= 0 || submitData[key]?.length === 0) {
                openErrorModal(submitData, key);
                hasError = true;
            }
        });

        setTimeout(async () => {
            if (hasError) {
                setIsSubCompanySubmitting(false);
                return;
            }

            const params = {
                subCompanyId: submitData._id,
                options: {
                    distanceAlertIsActive: submitData?.distanceAlertIsActive,
                    distanceLimit: submitData?.distanceLimit,
                    shiftControlIsActive: submitData?.shiftControlIsActive,
                    earliestShiftStart: submitData?.earliestShiftStart,
                    latestShiftEnd: submitData?.latestShiftEnd,
                    maxShiftPackageLimit: submitData?.maxShiftPackageLimit
                }
            };
            const result = await stores.companyStore.updateSubCompanyDistanceNotificationOptions(
                params
            );

            if (result) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setIsSubCompanySubmitting(false);
        }, 0);
    };

    const renderSwitch = key => {
        return (
            <Table.Column
                dataIndex={key}
                shouldCellUpdate={() => true}
                title={t(`fields.shiftOptions.${key}.short`, {ns: "common"})}
                render={(_, record, index) => {
                    const shift = subCompanyData.rows[index]?.options?.shift;
                    return (
                        <Switch
                            data-testid={key}
                            checked={shift[key]}
                            onChange={e => {
                                showSubmitButton[record._id] = true;
                                setSubCompanyData(prevData => {
                                    const newData = {...prevData};
                                    const newItem = {...newData.rows[index]};
                                    newItem.options.shift[key] = e;
                                    newData.rows[index] = newItem;
                                    return newData;
                                });
                            }}
                        />
                    );
                }}
            />
        );
    };

    const renderInput = (key, disabledKey) => {
        return (
            <Table.Column
                dataIndex={key}
                shouldCellUpdate={() => true}
                title={t(`fields.shiftOptions.${key}.short`, {ns: "common"})}
                render={(_, record, index) => {
                    const shift = subCompanyData.rows[index]?.options?.shift;
                    return (
                        <Input
                            disabled={!shift[disabledKey]}
                            type="number"
                            name={key}
                            data-testid={`${key}Input`}
                            min={0}
                            value={shift && shift[key]}
                            onChange={e => {
                                e.persist();
                                showSubmitButton[record._id] = true;
                                setSubCompanyData(prevData => {
                                    const newData = {...prevData};
                                    const newItem = {...newData.rows[index]};
                                    newItem.options.shift[key] = e.target?.value || "";
                                    newData.rows[index] = newItem;
                                    return newData;
                                });
                            }}
                        />
                    );
                }}
            />
        );
    };

    return (
        <SubCompanyContainer>
            <Table
                rowKey="_id"
                dataSource={subCompanyData?.rows}
                loading={subCompanyLoading}
                data-testid="subCompaniesTable"
                pagination={{
                    total: subCompanyData.dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                        history.push("/companies/shift-options/" + companyId + "?page=" + page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <Table.Column
                    dataIndex="name"
                    title={t("fields.subcompany", {ns: "common"})}
                    render={text => <span data-textid="name">{text}</span>}
                />
                <Table.Column
                    dataIndex="city"
                    title={t("fields.city", {ns: "common"})}
                    render={text => <span data-textid="city">{text}</span>}
                />
                <Table.Column
                    dataIndex={"upperRegionGroup"}
                    title={t("fields.upperGroup", {ns: "common"})}
                    render={text => <span data-textid="upperRegionGroup">{text}</span>}
                />
                <Table.Column
                    dataIndex={"regionGroup"}
                    title={t("fields.regionGroup", {ns: "common"})}
                    render={text => <span data-testid="regionGroup">{text?.name}</span>}
                />

                {renderSwitch("shiftControlIsActive")}
                {renderInput("earliestShiftStart", "shiftControlIsActive")}
                {renderInput("latestShiftEnd", "shiftControlIsActive")}
                {renderSwitch("distanceAlertIsActive")}
                {renderInput("distanceLimit", "distanceAlertIsActive")}
                {renderSwitch("packageLimitAlertIsActive")}
                {renderInput("maxShiftPackageLimit", "packageLimitAlertIsActive")}

                <Table.Column
                    dataIndex="_id"
                    shouldCellUpdate={() => true}
                    render={(text, _, index) =>
                        showSubmitButton[text] && (
                            <Button
                                disabled={isSubCompanySubmitting}
                                onClick={() => onSubCompanySubmit(index)}
                                type="primary"
                                data-testid="submitButton"
                            >
                                {t("actions.save", {ns: "common"})}
                            </Button>
                        )
                    }
                />
            </Table>
        </SubCompanyContainer>
    );
};

export default SubCompanyShiftOptions;
